import type { ReactNode } from 'react'
import { Bank as BankIcon } from '@phosphor-icons/react/dist/ssr/Bank'
import { Bug as BugIcon } from '@phosphor-icons/react/dist/ssr/Bug'
import { ChartBar as ChartBarIcon } from '@phosphor-icons/react/dist/ssr/ChartBar'
import { ChartLine as ChartLineIcon } from '@phosphor-icons/react/dist/ssr/ChartLine'
import { CurrencyDollar as CurrencyDollarIcon } from '@phosphor-icons/react/dist/ssr/CurrencyDollar'
import { Gear as SettingsIcon } from '@phosphor-icons/react/dist/ssr/Gear'
import { Headset as HeadsetIcon } from '@phosphor-icons/react/dist/ssr/Headset'
import { Lightbulb as LightbulbIcon } from '@phosphor-icons/react/dist/ssr/Lightbulb'
import { Question as QuestionIcon } from '@phosphor-icons/react/dist/ssr/Question'
import { Receipt as TransactionIcon } from '@phosphor-icons/react/dist/ssr/Receipt'
import { ShoppingBagOpen as ProductIcon } from '@phosphor-icons/react/dist/ssr/ShoppingBagOpen'
import { Storefront as StoreIcon } from '@phosphor-icons/react/dist/ssr/Storefront'
import { Tag as TagIcon } from '@phosphor-icons/react/dist/ssr/Tag'
import { Truck as TruckIcon } from '@phosphor-icons/react/dist/ssr/Truck'
import { Users as TeamIcon } from '@phosphor-icons/react/dist/ssr/Users'
import { Video as VideoIcon } from '@phosphor-icons/react/dist/ssr/Video'

import { paths } from '@/paths'

import { OMNIBAR_ENTITY_COLORS } from './omnibar-colors'

export interface CommandItem {
	label: string
	description?: string
	/** Synonyms and related terms used for search matching only — not shown in the UI */
	searchTerms?: readonly string[]
	href: string
	icon: ReactNode
	typeformId?: string
	accentColor?: string
}

export function getCommandItemSearchTarget(item: CommandItem): string {
	const parts = [item.label]
	if (item.description) parts.push(item.description)
	if (item.searchTerms?.length) parts.push(...item.searchTerms)
	return parts.join(' ')
}

export const QUICK_ACTIONS: CommandItem[] = [
	{
		label: 'Add product',
		href: paths.dashboard.products.add,
		icon: <ProductIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.product,
	},
	{
		label: 'Start restock',
		href: paths.dashboard.restocks.new,
		icon: <TruckIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.restock,
	},
	{
		label: 'Add promotion',
		href: paths.dashboard.promotions.create,
		icon: <TagIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.promotion,
	},
	{
		label: 'Manage pricing',
		href: paths.dashboard.products.pricing,
		icon: <CurrencyDollarIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.pricing,
	},
	{
		label: 'Review sales',
		href: paths.dashboard.analytics.sales,
		icon: <ChartBarIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.analytics,
	},
]

export const NAVIGATE_ITEMS: CommandItem[] = [
	{
		label: 'Stores',
		description: 'Manage stores and cabinets',
		searchTerms: ['locations', 'cabinets', 'machines'],
		href: paths.dashboard.stores.list,
		icon: <StoreIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.store,
	},
	{
		label: 'Products',
		description: 'Manage your product catalog and pricing',
		searchTerms: ['catalog', 'inventory', 'SKUs'],
		href: paths.dashboard.products.list,
		icon: <ProductIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.product,
	},
	{
		label: 'Transactions',
		description: 'Browse customer purchases and receipts',
		searchTerms: ['purchases', 'receipts', 'sales', 'orders'],
		href: paths.dashboard.transactions.list,
		icon: <TransactionIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.transaction,
	},
	{
		label: 'Marketing',
		description: 'Manage in-store video and screen content',
		searchTerms: ['videos', 'screens', 'advertising'],
		href: paths.dashboard.marketing.list,
		icon: <VideoIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.marketing,
	},
	{
		label: 'Promotions',
		description: 'Create and manage discounts',
		searchTerms: ['coupons', 'discounts', 'offers', 'campaigns'],
		href: paths.dashboard.promotions.list,
		icon: <TagIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.promotion,
	},
	{
		label: 'Restocks',
		description: 'Track product restocking',
		href: paths.dashboard.restocks.list,
		icon: <TruckIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.restock,
	},
	{
		label: 'Finances',
		description: 'View payouts, fees, and financial summaries',
		searchTerms: ['payouts', 'earnings', 'deposits'],
		href: paths.dashboard.finances.list,
		icon: <ChartLineIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.finance,
	},
]

export const ANALYTICS_ITEMS: CommandItem[] = [
	{
		label: 'Sales performance',
		description: 'Track sales trends and performance over time',
		href: paths.dashboard.analytics.sales,
		icon: <ChartBarIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.analytics,
	},
	{
		label: 'Daily sales',
		description: 'View day-by-day sales breakdowns',
		href: paths.dashboard.analytics.dailySales,
		icon: <ChartBarIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.analytics,
	},
	{
		label: 'Restock planning',
		description: 'Identify what to restock based on sales data',
		searchTerms: ['pick list', 'replenishment', 'shortages'],
		href: paths.dashboard.analytics.inventory,
		icon: <ChartBarIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.analytics,
	},
	{
		label: 'Earnings',
		description: 'Review revenue and earnings reports',
		href: paths.dashboard.analytics.earnings,
		icon: <ChartBarIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.analytics,
	},
	{
		label: 'Tax report',
		description: 'Export tax-related sales reporting',
		href: paths.dashboard.analytics.taxReport,
		icon: <ChartBarIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.analytics,
	},
]

export const HELP_ITEMS: CommandItem[] = [
	{
		label: 'Help center',
		description: 'Documentation and guides for using Micromart',
		searchTerms: ['learn'],
		href: paths.learn,
		icon: <QuestionIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.help,
	},
	{
		label: 'Report a bug',
		description: 'Submit a bug report to the Micromart team',
		href: paths.dashboard.help.typeform('ZFzITdAM'),
		typeformId: 'ZFzITdAM',
		icon: <BugIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.bug,
	},
	{
		label: 'Request a feature',
		description: 'Suggest a new feature or improvement',
		href: paths.dashboard.help.typeform('zyQsICLN'),
		typeformId: 'zyQsICLN',
		icon: <LightbulbIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.feature,
	},
]

export const ACCOUNT_ITEMS: CommandItem[] = [
	{
		label: 'Settings',
		description: 'Manage your account and preferences',
		href: paths.dashboard.settings.account,
		icon: <SettingsIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.settings,
	},
	{
		label: 'Team',
		description: 'Manage team members and roles',
		searchTerms: ['user'],
		href: paths.dashboard.settings.team,
		icon: <TeamIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.settings,
	},
	{
		label: 'Tax settings',
		description: 'Manage tax regions and rates',
		searchTerms: ['tax', 'GST', 'HST', 'VAT', 'sales tax'],
		href: paths.dashboard.settings.taxes.home,
		icon: <BankIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.settings,
	},
	{
		label: 'Support settings',
		description: 'Customize customer support contact info',
		searchTerms: ['feedback', 'contact', 'survey'],
		href: paths.dashboard.settings.support,
		icon: <HeadsetIcon size={18} />,
		accentColor: OMNIBAR_ENTITY_COLORS.settings,
	},
]

export const ALL_STATIC_ITEMS: CommandItem[] = [
	...QUICK_ACTIONS,
	...NAVIGATE_ITEMS,
	...ANALYTICS_ITEMS,
	...HELP_ITEMS,
	...ACCOUNT_ITEMS,
]

/** Character-sequence match on a single field (label or search term — not descriptions). */
function fuzzyMatchChars(query: string, target: string): number {
	const q = query.toLowerCase()
	const t = target.toLowerCase()
	if (t.includes(q)) return 0.85
	let qi = 0
	let consecutiveBonus = 0
	let lastMatchIdx = -2
	for (let ti = 0; ti < t.length && qi < q.length; ti++) {
		if (t[ti] === q[qi]) {
			if (ti === lastMatchIdx + 1) consecutiveBonus += 0.05
			lastMatchIdx = ti
			qi++
		}
	}
	if (qi < q.length) return 0
	return Math.min(0.74, 0.45 + consecutiveBonus)
}

/** Score a static nav/quick item against a query. Higher scores = stronger matches. */
export function scoreCommandItem(query: string, item: CommandItem): number {
	const q = query.trim().toLowerCase()
	if (!q) return 0

	const label = item.label.toLowerCase()
	const description = item.description?.toLowerCase() ?? ''
	const terms = (item.searchTerms ?? []).map((t) => t.toLowerCase())

	if (label === q) return 1
	if (label.includes(q)) return 0.98

	const labelWords = label.split(/\s+/)
	if (labelWords.some((w) => w === q)) return 0.96
	if (labelWords.some((w) => w.startsWith(q))) return 0.94

	if (terms.some((t) => t === q)) return 0.93
	if (terms.some((t) => t.includes(q))) return 0.91

	if (description.includes(q)) return 0.88

	let bestFuzzy = fuzzyMatchChars(q, label)
	for (const term of terms) {
		bestFuzzy = Math.max(bestFuzzy, fuzzyMatchChars(q, term))
	}
	return bestFuzzy
}

/** @deprecated Use scoreCommandItem — kept for tests if any */
export function fuzzyMatch(query: string, target: string): number {
	const q = query.toLowerCase()
	const t = target.toLowerCase()
	if (t.includes(q)) return 1
	return fuzzyMatchChars(query, target)
}
